import { useState } from 'react'
import type { FormEvent } from 'react'
import { Sparkles } from 'lucide-react'
import { Input } from '~/components/ui/input'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { CustomSelect } from '~/components/ui/custom-select'
import { AiDefinePanel } from '~/components/assessment/ai-define-panel'
import { ConstraintRadioGroup } from '~/components/assessment/constraint-radio-group'
import { useAssessmentCombinations } from '~/components/assessment/use-assessment-combinations'
import {
    ASSESSMENT_TYPE_DESCRIPTION,
    ASSESSMENT_TYPE_OPTIONS,
    CLASSIFICATION_DESCRIPTION,
    CLASSIFICATION_OPTIONS,
} from '~/components/assessment/labels'
import type {
    BusinessContext,
    BusinessContextUpsertRequest,
    CreateAssessmentRequest,
    DefineSlots,
} from '~/api/types'

export interface NewAssessmentFormProps {
    /** 생성 핸들러. */
    onCreate(value: CreateAssessmentRequest): void | Promise<void>
    /** 사업장 기본 프로필 저장 핸들러. */
    onSaveProfile?(
        value: BusinessContextUpsertRequest
    ): Promise<BusinessContext>
    activeProfile?: BusinessContext | null
    /** 취소(목록으로 복귀). */
    onCancel(): void
    creating?: boolean
}

/**
 * 새 평가 생성 폼.
 * 제목 + 업종 + 규모(일반/소규모) + 분류(선행요건/HACCP관리) + 단계(인증/정기)를 받는다.
 *
 * 업종 택소노미가 (규모·분류·단계) 파티션마다 달라 4축을 자유 조합하면 항목이 0인 죽은 조합이
 * 생긴다(예: 일반 규모 + 일반음식점 — 일반음식점은 소규모 HACCP 대상). 그래서 실존 조합
 * 매트릭스(GET /assessments/combinations)만으로 4축을 상호제약한다:
 * - 업종을 앵커로 자유 선택(전체 합집합).
 * - 규모·분류·단계는 선택한 업종과 실존 조합이 있는 값만 활성, 나머지는 비활성.
 * - 선택 충돌 시 종속 축을 유효값으로 자동 보정(사업장 기본값·AI 정의 경로 포함).
 *
 * 4축 상호제약 엔진과 관련 상태는 `useAssessmentCombinations` 훅이 보유한다.
 */
export function NewAssessmentForm({
    onCreate,
    onSaveProfile,
    activeProfile,
    onCancel,
    creating,
}: NewAssessmentFormProps) {
    const [title, setTitle] = useState('')
    const [saveProfile, setSaveProfile] = useState(true)
    const [aiOpen, setAiOpen] = useState(false)
    const {
        scale,
        setScale,
        industry,
        setIndustry,
        profileTitle,
        setProfileTitle,
        classification,
        setClassification,
        stage,
        setStage,
        ready,
        enabled,
        industryOptions,
    } = useAssessmentCombinations(activeProfile)

    // AI가 추론한 슬롯으로 폼 필드를 자동 채운다. null/미정의 슬롯은 건드리지 않는다.
    const applySlots = (slots: DefineSlots) => {
        if (slots.scale) setScale(slots.scale)
        if (slots.industry != null) setIndustry(slots.industry)
        if (
            slots.classification === '선행요건' ||
            slots.classification === 'HACCP관리'
        )
            setClassification(slots.classification)
        if (slots.assessmentType != null) setStage(slots.assessmentType)
        if (slots.title != null) setTitle(slots.title)
    }

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()
        const trimmed = title.trim()
        if (!trimmed) return
        const trimmedIndustry = industry.trim()
        if (saveProfile && onSaveProfile) {
            await onSaveProfile({
                title: profileTitle.trim() || trimmed,
                scale,
                industry: trimmedIndustry || undefined,
            })
        }
        await onCreate({
            title: trimmed,
            scale,
            classification,
            assessmentType: stage,
            industry: trimmedIndustry || undefined,
        })
    }

    return (
        <>
            <Card>
                <CardHeader className='flex flex-row items-center justify-between gap-2 space-y-0'>
                    <CardTitle>새 평가</CardTitle>
                    <Button
                        type='button'
                        variant={aiOpen ? 'secondary' : 'outline'}
                        onClick={() => setAiOpen((v) => !v)}
                        className='gap-1.5'>
                        <Sparkles className='size-4' />
                        AI로 정의
                    </Button>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className='space-y-4'>
                        <div className='space-y-1.5'>
                            <label
                                className='text-foreground text-sm font-medium'
                                htmlFor='new-title'>
                                평가 제목
                            </label>
                            <Input
                                id='new-title'
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder='예: 2026년 도시락 공장 자가 진단'
                                required
                            />
                        </div>

                        <div className='space-y-1.5'>
                            <label
                                className='text-foreground text-sm font-medium'
                                htmlFor='new-industry'>
                                업종
                            </label>
                            <CustomSelect
                                id='new-industry'
                                ariaLabel='업종'
                                value={industry}
                                options={industryOptions}
                                onValueChange={setIndustry}
                                searchable
                                searchPlaceholder='업종 검색…'
                                placeholder='업종을 검색·선택하세요'
                            />
                            <p className='text-muted-foreground text-xs'>
                                업종을 고르면 그에 맞는 규모·분류·단계만 선택할
                                수 있어요.
                            </p>
                        </div>

                        <ConstraintRadioGroup
                            legend='규모'
                            name='scale'
                            options={['일반', '소규모'] as const}
                            value={scale}
                            onChange={(value) => setScale(value)}
                            enabledSet={enabled.scale}
                            ready={ready}
                        />

                        <ConstraintRadioGroup
                            legend='분류'
                            name='classification'
                            options={CLASSIFICATION_OPTIONS}
                            value={classification}
                            onChange={(value) => setClassification(value)}
                            enabledSet={enabled.classification}
                            ready={ready}
                            descriptions={CLASSIFICATION_DESCRIPTION}
                        />

                        <ConstraintRadioGroup
                            legend='단계'
                            name='assessmentType'
                            options={ASSESSMENT_TYPE_OPTIONS}
                            value={stage}
                            onChange={(value) => setStage(value)}
                            enabledSet={enabled.stage}
                            ready={ready}
                            descriptions={ASSESSMENT_TYPE_DESCRIPTION}
                        />

                        <div className='border-border bg-muted/40 space-y-3 rounded-md border p-3'>
                            <label className='text-foreground flex items-center gap-2 text-sm font-medium'>
                                <input
                                    type='checkbox'
                                    checked={saveProfile}
                                    onChange={(e) =>
                                        setSaveProfile(e.target.checked)
                                    }
                                    className='accent-primary'
                                />
                                이 규모와 업종을 사업장 기본값으로 저장
                            </label>
                            {saveProfile ? (
                                <div className='space-y-1.5'>
                                    <label
                                        className='text-muted-foreground text-xs font-medium'
                                        htmlFor='profile-title'>
                                        사업장 프로필 이름
                                    </label>
                                    <Input
                                        id='profile-title'
                                        value={profileTitle}
                                        onChange={(e) =>
                                            setProfileTitle(e.target.value)
                                        }
                                        placeholder='예: 1호점, 본점, 도시락 공장'
                                    />
                                </div>
                            ) : null}
                            <p className='text-muted-foreground text-xs'>
                                저장된 기본값은 모의 평가, 상담, 변경 검토에서 우선
                                참고됩니다.
                            </p>
                        </div>

                        <div className='flex justify-end gap-2'>
                            <Button
                                type='button'
                                variant='ghost'
                                onClick={onCancel}
                                disabled={creating}>
                                취소
                            </Button>
                            <Button
                                type='submit'
                                disabled={creating || !title.trim()}>
                                {creating ? '생성 중…' : '평가 시작'}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>

            {aiOpen ? (
                <AiDefinePanel
                    onSlots={applySlots}
                    onClose={() => setAiOpen(false)}
                />
            ) : null}
        </>
    )
}
